import { MouseEvent, useCallback, useState } from 'react';
import { faSend } from '@fortawesome/pro-regular-svg-icons';
import {
  faCalendarClock,
  faChevronDown,
  faFileLines,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover } from '@mui/material';

type Props = {
  disabled: boolean;
  isSubmitting: boolean;
  onPost: () => void;
  onSaveDraft: () => void;
  onSchedule: () => void;
};

export const PostSplitButton = ({
  disabled,
  isSubmitting,
  onPost,
  onSaveDraft,
  onSchedule,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);
  const close = useCallback(() => setAnchorEl(null), []);

  const pick = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <>
      <div className="flex items-stretch overflow-hidden rounded-[10px] bg-[#350584] text-white disabled:opacity-50">
        <button
          type="button"
          disabled={disabled}
          onClick={onPost}
          className="flex items-center gap-2 py-3 pl-6 pr-4 text-base font-medium disabled:cursor-not-allowed disabled:opacity-50"
        >
          {!isSubmitting && <FontAwesomeIcon icon={faSend} />}
          {isSubmitting ? 'Posting...' : 'Post'}
        </button>
        <span aria-hidden className="my-3 w-px bg-white" />
        <button
          type="button"
          aria-label="More post options"
          disabled={disabled}
          onClick={open}
          className="px-4 text-base disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </button>
      </div>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '8px',
              borderRadius: '12px',
              boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.15)',
            },
          },
        }}
      >
        <div className="flex min-w-[220px] flex-col py-2">
          <button
            type="button"
            disabled={disabled}
            onClick={pick(onSaveDraft)}
            className="flex items-center gap-3 px-4 py-3 text-left text-base font-medium text-black hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faFileLines} fixedWidth />
            Save draft
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={pick(onSchedule)}
            className="flex items-center gap-3 px-4 py-3 text-left text-base font-medium text-black hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faCalendarClock} fixedWidth />
            Schedule post
          </button>
        </div>
      </Popover>
    </>
  );
};
